import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';

import { useCoverLotItemMeta } from '@/hooks/useCoverLotItemMeta';
import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogCoverLotsTransformed,
    getCatalogIsCatalogOnly,
    getCatalogIsTimedPlusBiddingOpen,
    getCatalogStartTime,
    getCatalogTimedPlusBiddingEndsFromTs,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { useTrackItemCardClicked } from '@/utils/amplitude/analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import type { CoverLotData } from '../types';

import { LARGE_IMAGE_SIZE } from '../CoverLotImage';
import { DesktopImageCluster } from './DesktopImageCluster';
import { DoneOverlay } from './DoneOverlay';
import { MobileImageCluster } from './MobileImageCluster';

type Props = {
    catalogId: number;
    isCatalogDone: boolean;
    onCountdownComplete?: () => void;
};

export const CoverLotsImageCluster = ({ catalogId, isCatalogDone, onCountdownComplete }: Props) => {
    const formatMessage = useAppFormatMessage();
    const trackItemCardClicked = useTrackItemCardClicked();
    const isMobile = useWindowIsMobile();

    const timedPlusBiddingOpen = useAppSelector((state) => getCatalogIsTimedPlusBiddingOpen(state, catalogId));
    const biddingEndsFromTs = useAppSelector((state) => getCatalogTimedPlusBiddingEndsFromTs(state, catalogId));
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));

    const countdownLabel = formatMessage([
        'catalogPage',
        'catalogStatus',
        isCatalogOnly
            ? isMobile
                ? 'previewOnlyMobileExperiment'
                : 'previewOnlyDesktopExperiment'
            : timedPlusBiddingOpen
              ? 'timedPlusBiddingEndsExperiment'
              : 'biddingStartsExperiment',
    ]);
    const countdownTime = (timedPlusBiddingOpen ? biddingEndsFromTs : saleStartTs) * 1000;

    const {
        primaryImage,
        primaryImageHighDpi,
        primaryItemId,
        primaryItemTitle,
        secondaryImage,
        secondaryImageHighDpi,
        secondaryItemId,
        secondaryItemTitle,
        tertiaryImage,
        tertiaryImageHighDpi,
        tertiaryItemId,
        tertiaryItemTitle,
    } = useAppSelector((state) => getCatalogCoverLotsTransformed(state, catalogId, LARGE_IMAGE_SIZE));

    const primaryMeta = useCoverLotItemMeta(primaryItemId);
    const secondaryMeta = useCoverLotItemMeta(secondaryItemId);
    const tertiaryMeta = useCoverLotItemMeta(tertiaryItemId);

    if (!primaryItemId) {
        return null;
    }

    const coverLots = [
        {
            catalogId: primaryMeta.catalogId,
            image: primaryImage,
            imageHighDpi: primaryImageHighDpi,
            itemId: primaryItemId,
            promoted: primaryMeta.promoted,
            title: primaryItemTitle,
            url: primaryMeta.url,
        },
        secondaryItemId && {
            catalogId: secondaryMeta.catalogId,
            image: secondaryImage,
            imageHighDpi: secondaryImageHighDpi,
            itemId: secondaryItemId,
            promoted: secondaryMeta.promoted,
            title: secondaryItemTitle,
            url: secondaryMeta.url,
        },
        tertiaryItemId && {
            catalogId: tertiaryMeta.catalogId,
            image: tertiaryImage,
            imageHighDpi: tertiaryImageHighDpi,
            itemId: tertiaryItemId,
            promoted: tertiaryMeta.promoted,
            title: tertiaryItemTitle,
            url: tertiaryMeta.url,
        },
    ].filter((lot): lot is CoverLotData => Boolean(lot));

    const doneOverlay = (
        <DoneOverlay
            isCatalogDone={isCatalogDone}
            isMobile={isMobile}
        />
    );

    if (isMobile) {
        return (
            <MobileImageCluster
                completionTime={countdownTime}
                countdownLabel={countdownLabel}
                coverLots={coverLots}
                doneOverlay={doneOverlay}
                hideCountdown={isCatalogOnly}
                onCountdownComplete={onCountdownComplete}
                trackItemCardClicked={trackItemCardClicked}
            />
        );
    }

    return (
        <DesktopImageCluster
            completionTime={countdownTime}
            countdownLabel={countdownLabel}
            coverLots={coverLots}
            doneOverlay={doneOverlay}
            hideCountdown={isCatalogOnly}
            onCountdownComplete={onCountdownComplete}
            trackItemCardClicked={trackItemCardClicked}
        />
    );
};
